import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Paper,
    Rating,
    TextField,
    Button,
    Box,
    CircularProgress,
    Alert,
    Snackbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createBorrowerReview, getTransaction } from '../services/api';

const StyledContainer = styled(Container)(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    boxShadow: '0 3px 5px 2px rgba(0, 0, 0, .1)',
}));

const BorrowerReviewPage = () => {
    const { transaction_id } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [transaction, setTransaction] = useState(null);
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    useEffect(() => {
        const fetchTransaction = async () => {
            try {
                const response = await getTransaction(transaction_id);
                setTransaction(response.data);

                // Verify that this is a completed transaction and the user is the lender
                if (response.data.status !== 'completed') {
                    setSnackbar({
                        open: true,
                        message: 'This transaction is not completed yet.',
                        severity: 'error'
                    });
                    setTimeout(() => navigate('/lending-bookings'), 3000);
                    return;
                }

                if (response.data.lender_id !== currentUser?.id) {
                    setSnackbar({
                        open: true,
                        message: 'You can only review borrowers for your own listings.',
                        severity: 'error'
                    });
                    setTimeout(() => navigate('/lending-bookings'), 3000);
                    return;
                }
            } catch (error) {
                console.error('Error fetching transaction:', error);
                setSnackbar({
                    open: true,
                    message: 'Error loading transaction details',
                    severity: 'error'
                });
            } finally {
                setLoading(false);
            }
        };

        fetchTransaction();
    }, [transaction_id, currentUser, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            await createBorrowerReview(transaction_id, {
                rating,
                comment: comment.trim() || null
            });

            setSnackbar({
                open: true,
                message: 'Review submitted successfully!',
                severity: 'success'
            });

            // Navigate back to lending bookings after a short delay
            setTimeout(() => {
                navigate('/lending-completed-bookings');
            }, 2000);
        } catch (error) {
            console.error('Error submitting review:', error);
            setSnackbar({
                open: true,
                message: 'Failed to submit review. Please try again.',
                severity: 'error'
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <StyledContainer maxWidth="sm">
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <CircularProgress />
                </Box>
            </StyledContainer>
        );
    }

    return (
        <StyledContainer maxWidth="sm">
            <StyledPaper>
                <Typography variant="h5" gutterBottom>
                    Rate Your Experience
                </Typography>

                {transaction && (
                    <Box mb={4}>
                        <Typography variant="subtitle1" gutterBottom>
                            Reviewing: {transaction.listing_title}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Borrower: {transaction.borrower_name}
                        </Typography>
                    </Box>
                )}

                <form onSubmit={handleSubmit}>
                    <Box mb={4}>
                        <Typography component="legend" gutterBottom>
                            Rate your experience with this borrower
                        </Typography>
                        <Rating
                            name="rating"
                            value={rating}
                            onChange={(event, newValue) => {
                                setRating(newValue);
                            }}
                            size="large"
                        />
                    </Box>

                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        label="Add a comment (optional)"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        sx={{ mb: 4 }}
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={submitting || !rating}
                    >
                        {submitting ? 'Submitting...' : 'Submit Review'}
                    </Button>
                </form>
            </StyledPaper>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </StyledContainer>
    );
};

export default BorrowerReviewPage;